/*
 * Wallet and daily tickets guide  ·  assets/hoh-earn-tickets.js
 *
 * The resource guide for the bottom-right wallet pill. It leads with
 * every spendable balance and the real place each resource is used,
 * then keeps the daily ticket quests and Jan's update underneath.
 *
 *   1. Tickets are raffle entries for a free monthly portrait
 *   2. There are TWO daily quests, totaling 4 tickets per day
 *   3. State-aware primary CTA points them at the next thing to do
 *
 * Two quests:
 *   Quest 1: Submit a hero (+1 ticket), capped 1/day
 *   Quest 2: Explore the Hall (+3 tickets), the 10/30/50 heart
 *            wander milestones in the Fresh Faces section
 *
 * Both feed the same character pool and roll into the monthly draw.
 *
 * R793 (2026-09-22): in the live Hall (the R793B doors installed) the
 * guide follows Jan's new rule instead: each of your own heroes takes one
 * ticket a day on its own page, nothing is earned or held, and hearts are
 * favourites with no balance. See renderR793(). The quest ladder above is
 * what legacy mode still shows.
 *
 * R150: the Daily Bounty Board was fully removed from this file. It
 * had been inert since R140-Z (every entry point a no-op). All of its
 * code, the board lightbox, the hint system, the sticky bar, is gone.
 *
 * Public:
 *   window.hohOpenEarnTickets()   open the modal
 *   window.hohCloseEarnTickets()  close
 */
(function(){
  'use strict';

  var ROOT = null;
  var LAST_FOCUS = null;
  var CLOSE_TIMER = 0;
  var BODY_OVERFLOW = '';
  var SYNC_SEQ = 0;
  var resourceSyncState = 'idle';
  var resourceSyncMessage = '';
  var resourceActionState = { kind:'', state:'', message:'' };

  function isOpen(){
    return !!(ROOT && ROOT.classList.contains('hoh-et-open') && ROOT.style.display !== 'none');
  }

  function isVisible(){
    return !!(ROOT && ROOT.style.display !== 'none');
  }

  function reducedMotion(){
    try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); }
    catch(_){ return false; }
  }

  function focusables(){
    if(!ROOT) return [];
    return Array.prototype.slice.call(ROOT.querySelectorAll(
      'button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
    )).filter(function(el){
      return !el.hidden && el.getAttribute('aria-hidden') !== 'true' && el.getClientRects().length > 0;
    });
  }

  function focusDialog(){
    if(!isOpen()) return;
    var sheet = ROOT.querySelector('.hoh-et-sheet');
    if(!sheet) return;
    try { sheet.focus({ preventScroll:true }); } catch(_){ try { sheet.focus(); } catch(__){} }
  }

  function ensureRoot(){
    if(ROOT) return ROOT;
    ROOT = document.createElement('div');
    ROOT.id = 'hoh-et-modal';
    ROOT.className = 'hoh-et-modal';
    ROOT.style.display = 'none';
    document.body.appendChild(ROOT);
    /* Click-off to dismiss (desktop + mobile). The inline backdrop
       onclick was unreliable, so we own the outside-click here at the
       persistent ROOT (survives render() re-builds of innerHTML). Guard:
       only close when the press STARTED and ENDED outside the sheet, so a
       text drag that begins inside the panel and releases on the backdrop
       never closes it by accident. */
    var _downOutside = false;
    ROOT.addEventListener(window.PointerEvent ? 'pointerdown' : 'mousedown', function(e){
      _downOutside = !(e.target && e.target.closest && e.target.closest('.hoh-et-sheet'));
    });
    ROOT.addEventListener('click', function(e){
      var insideSheet = e.target && e.target.closest && e.target.closest('.hoh-et-sheet');
      if(!insideSheet && _downOutside) close();
      _downOutside = false;
      var retry = e.target && e.target.closest ? e.target.closest('[data-et-retry]') : null;
      if(retry){ e.preventDefault(); refreshResources(); return; }
      var action = e.target && e.target.closest ? e.target.closest('[data-et-resource]') : null;
      if(action){ e.preventDefault(); useResource(action.getAttribute('data-et-resource')); }
    });
    ROOT.addEventListener('keydown', function(e){
      if(!isOpen()) return;
      if(e.key === 'Escape'){
        e.preventDefault(); close(); return;
      }
      if(e.key !== 'Tab') return;
      var list = focusables();
      if(!list.length){ e.preventDefault(); focusDialog(); return; }
      var first = list[0], last = list[list.length - 1];
      if(e.shiftKey && (document.activeElement === first || document.activeElement === ROOT.querySelector('.hoh-et-sheet'))){
        e.preventDefault(); last.focus();
      } else if(!e.shiftKey && document.activeElement === last){
        e.preventDefault(); first.focus();
      }
    });
    return ROOT;
  }

  function close(opts){
    if(!ROOT) return;
    opts = opts || {};
    var restore = opts.restore !== false;
    var returnTo = LAST_FOCUS;
    LAST_FOCUS = null;
    ROOT.classList.remove('hoh-et-open');
    if(CLOSE_TIMER) clearTimeout(CLOSE_TIMER);
    CLOSE_TIMER = setTimeout(function(){
      if(ROOT) ROOT.style.display = 'none';
      document.body.style.overflow = BODY_OVERFLOW;
      if(restore && returnTo && returnTo.isConnected){
        try { returnTo.focus({ preventScroll:true }); } catch(_){ try { returnTo.focus(); } catch(__){} }
      }
    }, reducedMotion() ? 0 : 220);
  }
  window.hohCloseEarnTickets = close;

  function esc(s){
    /* Always use regex-based escaping. The window.esc fallback uses
       textContent->innerHTML, which does NOT escape " (browsers only
       encode " when serializing attributes, not text nodes). When the
       result is then embedded inside an HTML attribute wrapped in "..."
       the inner unescaped " terminates the attribute early, breaking
       the rest of the markup. The bounty 2-of-3 hint hit this exact
       bug: tier2Text contains class="x" inside its <span>, esc via
       window.esc preserved the ", and the data-text attribute parsed
       only as far as "A name carrying " before the inner " ended it. */
    if(s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* Read combined state. R140-Z: 2 daily quests:
       - Submit a hero (+1 ticket)
       - Explore the Hall (+3 tickets) — claimed at the 10 / 30 / 50
         heart wander milestones in the Fresh Faces section. R406 slid
         that ladder down from 20/40/60 and put a free 20-heart pip on
         the very start of the bar.
     The Daily Bounty was fully removed as a ticket source. Daily max
     stays 4 tickets (1 submit + 3 wander). */
  function getState(){
    var st = window.hohTicketState || {};
    /* Wander progress (heart count) for the in-panel progress bar
       display. Optional — falls back to a 0/50 stub if fresh-faces
       isn\'t loaded. */
    var wanderProgress;
    if(typeof window.hohGetWanderQuestProgress === 'function'){
      try { wanderProgress = window.hohGetWanderQuestProgress(); } catch(_){ wanderProgress = null; }
    }
    if(!wanderProgress){
      wanderProgress = { count: 0, target: 50, done: false };
    }
    /* R61: Tickets EARNED come from CLAIMED milestones, not heart
       count. The old code derived `wanderTicketsEarned` from raw
       hearts (>=10 + >=20), which credited tickets the user hadn\'t
       actually banked yet. New flow: read the date-keyed claim
       flags via hohGetWanderQuestClaimed. Wander caps at 3 tickets,
       which land on the 10, 30 and 50 heart pips. */
    var wanderClaimed = null;
    if(typeof window.hohGetWanderQuestClaimed === 'function'){
      try { wanderClaimed = window.hohGetWanderQuestClaimed(); } catch(_){ wanderClaimed = null; }
    }
    var wanderTicketsEarned = (wanderClaimed && typeof wanderClaimed.count === 'number') ? wanderClaimed.count : 0;
    /* R140-Z: Ticket ladder — 1 from submit + 3 from wander (the
       10 / 30 / 50 heart milestones). Bounty fully removed. */
    var wanderDone = wanderTicketsEarned >= 3;
    var submittedDone = !!st.submitted_today;
    if(!submittedDone && typeof window.hohHasSubmittedToday === 'function'){
      try { submittedDone = !!window.hohHasSubmittedToday(); } catch(_){}
    }
    var allDone = submittedDone && wanderDone;
    var dailyTotal = (submittedDone ? 1 : 0) + wanderTicketsEarned;
    return {
      submitted: submittedDone,
      tinder: wanderDone, /* keep field name for back-compat */
      wanderCount: wanderProgress.count,
      wanderTarget: wanderProgress.target,
      wanderTicketsEarned: wanderTicketsEarned,
      wanderClaimed: wanderClaimed,
      bounty: true, /* R140-Z: legacy field — treat as always done so old paths skip bounty CTAs. */
      bountyHint: '',
      bountyIsOwn: false,
      bothDone: allDone,
      total: dailyTotal,
      max: 4
    };
  }

  /* R140-Z: Primary CTA priority — submission first, then explore the
     hall (the 3-ticket wander quest). Bounty was fully removed, so the
     ladder is just submit → wander. */
  function primaryQuest(state){
    if(state.bothDone) return null;
    if(!state.submitted) return 'submit';
    if(!state.tinder) return 'tinder';
    return null;
  }

  /* Hearts-hidden bonus row. Lives below the quest list inside the
     Daily Tickets modal. Behaves like a quest row visually but is
     not gated by daily completion — it's a "did you know" prompt
     plus a one-tap +3 heart claim that opens the rules modal so
     the user learns where hearts hide. The click flow:
       1) Animate +3 hearts onto the wallet pill
       2) Close this modal
       3) Open the rules / "How it Works" modal
       4) Scroll to the hearts section in that modal
     The +3 grant is a one-per-day bonus gated by localStorage so a
     user can't keep clicking for free hearts. */
  var BONUS_HEART_DAILY_KEY = 'hoh-bonus-heart-claim-date';
  function bonusHeartRowHtml(){
    var claimedToday = false;
    try {
      var d = new Date().toISOString().slice(0,10);
      claimedToday = localStorage.getItem(BONUS_HEART_DAILY_KEY) === d;
    } catch(_){}
    /* Centered layout — no side icons. The reward indicator is the
       tri-heart bundle (3 hearts together) below the subtext, so
       the action ("+3 hearts when you click") reads at a glance.
       Once claimed today, the indicator collapses to a check mark
       beside the count. */
    var indicatorHtml = claimedToday
      ? '<span class="hoh-et-bonus-indicator hoh-et-bonus-indicator-done">' +
          '<span class="hoh-et-bonus-num">Claimed</span>' +
          '<span class="hoh-et-bonus-check">&check;</span>' +
        '</span>'
      : '<span class="hoh-et-bonus-indicator">' +
          '<span class="hoh-et-bonus-trihearts" aria-hidden="true">' +
            '<span class="hoh-et-bonus-trihearts-l">&#9829;&#xFE0E;</span>' +
            '<span class="hoh-et-bonus-trihearts-c">&#9829;&#xFE0E;</span>' +
            '<span class="hoh-et-bonus-trihearts-r">&#9829;&#xFE0E;</span>' +
          '</span>' +
          '<span class="hoh-et-bonus-num">Claim</span>' +
        '</span>';
    return '<div class="hoh-et-quest hoh-et-quest-hearts-hidden hoh-et-quest-hearts-hidden-centered' + (claimedToday ? ' hoh-et-quest-done' : ' hoh-et-quest-clickable') + '" ' +
      'role="button" tabindex="0" ' +
      'onclick="hohEtClaimBonusHearts(this)" ' +
      'onkeydown="if(event.key===\'Enter\'||event.key===\' \'){event.preventDefault();hohEtClaimBonusHearts(this);}">' +
      '<div class="hoh-et-quest-title">Hearts hidden across the Hall</div>' +
      '<div class="hoh-et-quest-sub">Find the glowing hearts tucked around the Hall.</div>' +
      indicatorHtml +
    '</div>';
  }
  /* Claim handler — R94: Two-stage flow.
       First tap (not yet claimed today): grant +3 hearts with fly
         animation, repaint the row to show "+3 hearts ✓", and STAY
         in the modal. The user gets the reward without being shoved
         into a rules modal they didn't ask for.
       Subsequent taps (already claimed today): open the rules modal
         scrolled to the hearts section so the user can learn where
         hearts hide. The row is now their info button.
     This swap means the chip works as both a reward AND a teach
     surface, but in the order users actually want: reward first. */
  window.hohEtClaimBonusHearts = function(rowEl){
    var d;
    try { d = new Date().toISOString().slice(0,10); } catch(_){}
    var alreadyClaimed = false;
    try { alreadyClaimed = localStorage.getItem(BONUS_HEART_DAILY_KEY) === d; } catch(_){}
    /* SECOND-tap path: already claimed → open the rules modal. */
    if(alreadyClaimed){
      try { if(typeof window.hohEtExit === 'function') window.hohEtExit('heart-rules'); } catch(_){}
      return;
    }
    /* FIRST-tap path: grant the hearts, fly them to the wallet pill,
       and repaint the row to the claimed (✓) state IN PLACE without
       closing the modal. */
    try {
      if(typeof window.hohAddHeartsToBank === 'function') window.hohAddHeartsToBank(3, 'earn_task');
      if(d) localStorage.setItem(BONUS_HEART_DAILY_KEY, d);
    } catch(_){}
    /* Heart-fly: spawn 3 heart sprites at the row, animate them to
       the header pill. Reuses the global helper if present. */
    try {
      if(typeof window.hohFlyHeartsToPill === 'function' && rowEl){
        window.hohFlyHeartsToPill(rowEl, 3);
      } else {
        /* Fallback: just pulse the pill so the user still gets a
           visual cue even if the global flyer isn't loaded. */
        var pill = document.getElementById('hoh-tk-header') || document.getElementById('hoh-heart-remaining');
        if(pill){
          pill.classList.remove('hoh-tk-heart-pulse');
          void pill.offsetWidth;
          pill.classList.add('hoh-tk-heart-pulse');
          setTimeout(function(){ pill.classList.remove('hoh-tk-heart-pulse'); }, 900);
        }
      }
    } catch(_){}
    if(typeof window.hohShowHeartToast === 'function'){
      try { window.hohShowHeartToast('Glowing heart found', 'Claimed for today.', true); } catch(_){}
    }
    /* Repaint the row in-place to the claimed state so the user sees
       the +3 ✓ chip and the row's onclick now opens the rules. */
    try {
      var newRow = document.createElement('div');
      newRow.innerHTML = bonusHeartRowHtml();
      var fresh = newRow.firstChild;
      if(rowEl && rowEl.parentNode && fresh){
        rowEl.parentNode.replaceChild(fresh, rowEl);
      }
    } catch(_){}
  };

  function questRowHtml(opts){
    var done = !!opts.done;
    var stateClass = done ? ' hoh-et-quest-done' : '';
    if(opts.interactive) stateClass += ' hoh-et-quest-clickable';
    /* Optional flavor class — passed by callers that want the row to
       glow gold (the bounty quest does this when not yet solved so
       users notice it as the day's distinctive task). */
    if(opts.flavor) stateClass += ' hoh-et-quest-' + opts.flavor;
    var icon = done
      ? '<span class="hoh-et-quest-check" aria-hidden="true">&check;</span>'
      : '<span class="hoh-et-quest-num" aria-hidden="true">' + opts.num + '</span>';
    var attrs = opts.interactive
      ? ' role="button" tabindex="0" data-et-focus="quest-' + esc(opts.num) + '" onclick="' + opts.onclick + '" onkeydown="if(event.key===\'Enter\'||event.key===\' \'){event.preventDefault();' + opts.onclick + ';}"'
      : '';
    return '<div class="hoh-et-quest' + stateClass + '"' + attrs + '>' +
      '<div class="hoh-et-quest-icon">' + icon + '</div>' +
      '<div class="hoh-et-quest-body">' +
        '<div class="hoh-et-quest-title">' + opts.title + '</div>' +
        '<div class="hoh-et-quest-sub">' + opts.sub + '</div>' +
      '</div>' +
      '<div class="hoh-et-quest-reward">' + (done
        ? '<span class="hoh-et-quest-reward-done">+' + opts.reward + ' &check;</span>'
        : '<span class="hoh-et-quest-reward-open"><span class="hoh-tk-icon" aria-hidden="true"></span>+' + opts.reward + '</span>') + '</div>' +
    '</div>';
  }

  /* Build a CTA button. Primary and secondary share the same visual
     format (label + reward badge). Submit a Hero gets a second pill
     reward badge for the +7 hearts submission bonus so the user
     sees both rewards at a glance — the ticket and the heart bank
     bump that fires after a successful insert. */
  /* R227: Reward badges removed from the action buttons — the quest
     rows above already state what each earns, so repeating it on the
     buttons was clutter. Buttons are now plain labels. */
  function ctaButton(label, onclick, kind){
    var cls = (kind === 'primary') ? 'hoh-et-btn hoh-et-btn-primary' : 'hoh-et-btn hoh-et-btn-secondary';
    var focusKey = /hall/i.test(label) ? 'profile-cta' : 'submit-cta';
    return '<button type="button" class="' + cls + '" data-et-focus="' + focusKey + '" onclick="' + onclick + '">' + label + '</button>';
  }

  /* Keep the remaining submission action; completed quests need no extra CTA. */
  function ctaHtml(state){
    if(state.submitted) return '';
    var openSubmit = "if(window.hohEtExit)hohEtExit('submit');";
    return '<div class="hoh-et-cta-stack">' + ctaButton('Submit a Hero', openSubmit, 'primary') + '</div>';
  }

  function whole(n){
    n = Number(n);
    return isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
  }

  function resourceSnapshot(){
    var W = window.HOHWallet || {};
    var packs = 0;
    try {
      if(typeof W.packsUnopened === 'function') packs = whole(W.packsUnopened());
      else {
        var mirror = window.__hohBgUnlimited || {};
        var account = (typeof W.accountEmail === 'function') ? String(W.accountEmail() || '') : '';
        if(mirror && mirror.packs_unopened != null && (!mirror.account || !account || String(mirror.account) === account)){
          packs = whole(mirror.packs_unopened);
        }
      }
    } catch(_){ packs = 0; }
    var crystals = 0, hearts = 0, bg = 0, tickets = 0;
    try { crystals = (typeof W.crystals === 'function') ? whole(W.crystals()) : 0; } catch(_){}
    try {
      hearts = (typeof W.hearts === 'function') ? whole(W.hearts()) :
        ((typeof window.heartsRemainingToday === 'function') ? whole(window.heartsRemainingToday()) : whole(localStorage.getItem('hoh-heart-bank-v1')));
    } catch(_){}
    try { bg = (typeof window.hohGetBgTickets === 'function') ? whole(window.hohGetBgTickets()) : 0; } catch(_){}
    try { tickets = (typeof window.hohGetUnspentTickets === 'function') ? whole(window.hohGetUnspentTickets()) : 0; } catch(_){}
    return { packs:packs, crystals:crystals, bg:bg, tickets:tickets, hearts:hearts };
  }

  function resourceIcon(kind){
    if(kind === 'packs'){
      return '<span class="hoh-et-pack-stack" aria-hidden="true"><i></i><i></i><i></i></span>';
    }
    if(kind === 'crystals'){
      try {
        if(window.HOHWallet && typeof window.HOHWallet.icon === 'function') return window.HOHWallet.icon(22, false);
      } catch(_){}
      return '<span class="hoh-et-resource-fallback" aria-hidden="true">&#10022;</span>';
    }
    if(kind === 'bg') return '<span class="hoh-et-resource-fallback hoh-et-resource-swords" aria-hidden="true">&#9876;&#xFE0E;</span>';
    if(kind === 'tickets') return '<span class="hoh-tk-icon" aria-hidden="true"></span>';
    return '<span class="hoh-et-resource-heart" aria-hidden="true">&#9829;&#xFE0E;</span>';
  }

  function resourceRow(kind, name, count, detail, action){
    var busy = resourceActionState.kind === kind && resourceActionState.state === 'loading';
    var stateClass = count > 0 ? ' has-balance' : ' is-empty';
    if(resourceActionState.kind === kind && resourceActionState.state) stateClass += ' is-' + resourceActionState.state;
    return '<button type="button" class="hoh-et-resource hoh-et-resource-' + kind + stateClass + '" data-et-resource="' + kind + '"' +
      (busy ? ' disabled aria-busy="true"' : '') + ' aria-label="' + esc(name + ': ' + count + '. ' + action) + '">' +
        '<span class="hoh-et-resource-icon">' + resourceIcon(kind) + '</span>' +
        '<span class="hoh-et-resource-copy"><span class="hoh-et-resource-name">' + name + '</span>' +
          '<span class="hoh-et-resource-detail">' + detail + '</span></span>' +
        '<span class="hoh-et-resource-end"><strong>' + count.toLocaleString() + '</strong>' +
          '<span>' + (busy ? 'Opening...' : action) + '</span></span>' +
      '</button>';
  }

  function resourceStatusHtml(){
    var state = resourceSyncState;
    var message = resourceSyncMessage;
    if(resourceActionState.state === 'error'){
      state = 'error'; message = resourceActionState.message || 'That action is still loading. Try again.';
    } else if(resourceActionState.state === 'success'){
      state = 'success'; message = resourceActionState.message || 'Your resources are updated.';
    }
    if(state === 'loading'){
      return '<div class="hoh-et-sync is-loading" role="status" aria-live="polite"><span aria-hidden="true"></span>Checking your saved balances...</div>';
    }
    if(state === 'error'){
      return '<div class="hoh-et-sync is-error" role="alert"><span>' + esc(message || 'Could not refresh your resources. The last saved balances are shown.') + '</span>' +
        '<button type="button" data-et-retry>Try again</button></div>';
    }
    if(state === 'success'){
      return '<div class="hoh-et-sync is-success" role="status" aria-live="polite">' + esc(message || 'Balances updated.') + '</div>';
    }
    return '<div class="hoh-et-sync" aria-live="polite"></div>';
  }

  function resourceGuideHtml(){
    var r = resourceSnapshot();
    var packDetail = r.packs > 0 ? 'Open one now. The rest stay saved.' : 'Buy a pack or claim the daily pack in the Shop.';
    return '<section class="hoh-et-resource-section" aria-labelledby="hoh-et-resources-title"' + (resourceSyncState === 'loading' ? ' aria-busy="true"' : '') + '>' +
      '<div class="hoh-et-section-head"><h2 id="hoh-et-resources-title">What you hold</h2><span>Tap a row to use it</span></div>' +
      '<div class="hoh-et-resources">' +
        resourceRow('packs', 'Unopened packs', r.packs, packDetail, r.packs > 0 ? 'Open one' : 'View packs') +
        resourceRow('crystals', 'Mythic crystals', r.crystals, 'Buy more or claim today\'s free crystal in the Shop.', 'View crystals') +
        resourceRow('bg', 'Battlegrounds entries', r.bg, 'Each entry starts one Battlegrounds run.', 'Go to battle') +
        resourceRow('tickets', 'Spendable tickets', r.tickets, 'Give these to heroes you submitted.', 'Give tickets') +
        resourceRow('hearts', 'Hearts', r.hearts, 'Spend these on packs, crystals, and Battlegrounds entries.', 'Open Shop') +
      '</div>' + resourceStatusHtml() +
    '</section>';
  }

  function rememberRenderFocus(){
    var active = document.activeElement;
    if(!ROOT || !active || !ROOT.contains(active)) return null;
    var list = focusables();
    return {
      id:active.id || '',
      resource:active.getAttribute && active.getAttribute('data-et-resource'),
      retry:active.hasAttribute && active.hasAttribute('data-et-retry'),
      focus:active.getAttribute && active.getAttribute('data-et-focus'),
      index:list.indexOf(active),
      sheet:active.classList && active.classList.contains('hoh-et-sheet')
    };
  }

  function restoreRenderFocus(key){
    if(!key || !ROOT || !isOpen()) return;
    var next = key.id ? document.getElementById(key.id) : null;
    if(!next && key.resource) next = ROOT.querySelector('[data-et-resource="' + key.resource + '"]');
    if(!next && key.retry) next = ROOT.querySelector('[data-et-retry]');
    if(!next && key.focus) next = ROOT.querySelector('[data-et-focus="' + key.focus + '"]');
    if(!next && key.index >= 0){
      var list = focusables();
      next = list[Math.min(key.index, list.length - 1)] || null;
    }
    if(!next || key.sheet) next = ROOT.querySelector('.hoh-et-sheet');
    if(next && !next.disabled){ try { next.focus({ preventScroll:true }); } catch(_){ try { next.focus(); } catch(__){} } }
  }

  function focusSoon(selector){
    var tries = 0;
    function find(){
      var target = null;
      try { target = document.querySelector(selector); } catch(_){}
      if(target){
        try { target.scrollIntoView({ block:'center', behavior:'auto' }); } catch(_){}
        var control = target.matches && target.matches('button,a,[tabindex]') ? target : target.querySelector && target.querySelector('button:not([disabled]),a[href],[tabindex]:not([tabindex="-1"])');
        if(control){ try { control.focus({ preventScroll:true }); } catch(_){ try { control.focus(); } catch(__){} } }
        return;
      }
      if(++tries < 24) setTimeout(find, 100);
    }
    find();
  }

  function afterClose(fn){
    close({ restore:false });
    setTimeout(function(){ try { fn(); } catch(_){} }, reducedMotion() ? 0 : 230);
  }

  /* Low-level tab switches only repaint the dashboard. Enter through its
     public door first so the page-mode class reveals that dashboard. */
  function enterYourHall(){
    if(document.body.classList.contains('hoh-page-mode-yours')) return true;
    if(typeof window.hohOpenYourHallProfile === 'function'){
      try { return window.hohOpenYourHallProfile() !== false; } catch(_){ return false; }
    }
    if(typeof window.hohYhToggleClick === 'function'){
      try { return window.hohYhToggleClick() !== false; } catch(_){ return false; }
    }
    return false;
  }

  function openProfileTarget(){
    if(typeof window.hohOpenYourHallProfile === 'function'){
      try { return window.hohOpenYourHallProfile() !== false; } catch(_){ return false; }
    }
    if(!enterYourHall() || typeof window.hohYourHallSwitchView !== 'function') return false;
    try { return window.hohYourHallSwitchView('profile') !== false; } catch(_){ return false; }
  }

  function openShopTarget(kind){
    var named = kind === 'crystals' ? window.hohOpenCrystalShop : (kind === 'packs' ? window.hohOpenPackShop : null);
    if(typeof named === 'function'){
      try { if(named() !== false) return true; } catch(_){}
    }
    if(!enterYourHall() || typeof window.hohYourHallSwitchView !== 'function') return false;
    try { if(window.hohYourHallSwitchView('shop') === false) return false; } catch(_){ return false; }
    if(kind === 'crystals') focusSoon('#ffshop-xtal-mod [data-xtal-buy],#ffshop-xtal-mod [data-xtal-daily],#ffshop-xtal-mod .ffshop-how-link');
    else if(kind === 'packs') focusSoon('.ffshop-shelf .ffshop-open-btn[data-open="hearts"],.ffshop-shelf [data-daily-pack]');
    else focusSoon('[data-yh-subtab="shop"],#ffshop-pack-mod button,#ffshop-xtal-mod button');
    return true;
  }

  function routeAvailable(kind){
    var hallDoor = document.body.classList.contains('hoh-page-mode-yours') ||
      typeof window.hohOpenYourHallProfile === 'function' || typeof window.hohYhToggleClick === 'function';
    if(kind === 'packs') return typeof window.hohOpenPackShop === 'function' || (hallDoor && typeof window.hohYourHallSwitchView === 'function');
    if(kind === 'crystals') return typeof window.hohOpenCrystalShop === 'function' || (hallDoor && typeof window.hohYourHallSwitchView === 'function');
    if(kind === 'hearts') return hallDoor && typeof window.hohYourHallSwitchView === 'function';
    if(kind === 'bg') return hallDoor && (typeof window.hohYourHallSetEventsTab === 'function' || typeof window.hohYourHallSwitchView === 'function');
    return hallDoor;
  }

  /* Secondary daily links use the same close boundary as wallet routes. This
     keeps the departing dialog from restoring focus over the next surface. */
  window.hohEtExit = function(kind){
    afterClose(function(){
      if(kind === 'submit'){
        if(typeof window.hohOpenForm === 'function') window.hohOpenForm();
      } else if(kind === 'profile'){
        if(!openProfileTarget() && typeof window.hohYhToggleClick === 'function') window.hohYhToggleClick();
      } else if(kind === 'rules' || kind === 'heart-rules'){
        if(typeof window.hohOpenRules === 'function') window.hohOpenRules();
        if(kind === 'heart-rules') setTimeout(function(){
          try {
            var rulesBody = document.querySelector('.hoh-rules-body');
            if(!rulesBody) return;
            var labels = rulesBody.querySelectorAll('.hoh-rules-section-label');
            for(var i = 0; i < labels.length; i++){
              if(/hearts/i.test(labels[i].textContent || '')){
                if(labels[i].scrollIntoView) labels[i].scrollIntoView({ behavior:'smooth', block:'start' });
                break;
              }
            }
          } catch(_){}
        }, 240);
      } else if(kind === 'explore'){
        try { if(typeof window.hohYourHallSwitchView === 'function') window.hohYourHallSwitchView('events'); } catch(_){}
        setTimeout(function(){
          var el = document.getElementById('hoh-fresh-faces');
          if(el && el.scrollIntoView) el.scrollIntoView({ behavior:'smooth', block:'center' });
        }, 250);
      }
    });
  };

  function actionError(kind, message, reopen){
    resourceActionState = { kind:kind, state:'error', message:message };
    if(reopen){
      setTimeout(function(){
        if(typeof window.hohOpenEarnTickets === 'function') window.hohOpenEarnTickets({ skipResourceSync:true });
      }, reducedMotion() ? 0 : 40);
    }
    try { if(typeof window.hohShowHeartToast === 'function') window.hohShowHeartToast('Wallet', message); } catch(_){}
  }

  function useResource(kind){
    var r = resourceSnapshot();
    resourceActionState = { kind:'', state:'', message:'' };
    if(kind === 'packs' && r.packs > 0 && typeof window.hohShopOpenSavedPacks === 'function'){
      resourceActionState = { kind:'packs', state:'loading', message:'Opening one saved pack...' };
      render();
      afterClose(function(){
        var calledBack = false;
        var done = function(ok, message){
          calledBack = true;
          if(ok === false){
            actionError('packs', message || 'That pack could not be opened. It is still saved.', true);
            return;
          }
          resourceActionState = { kind:'packs', state:'success', message:'Pack opened. Your saved count is updated.' };
          resourceSyncState = 'success';
          resourceSyncMessage = 'Pack opened. Your saved count is updated.';
          try { if(window.HOHWallet && typeof window.HOHWallet.sync === 'function') window.HOHWallet.sync(true); } catch(_){}
        };
        var started;
        try { started = window.hohShopOpenSavedPacks(1, done); }
        catch(_){ started = false; }
        if(started === false && !calledBack) actionError('packs', 'The pack opener is busy. Try again.', true);
      });
      return;
    }
    if(!routeAvailable(kind)){
      actionError(kind, 'That part of the Hall is still loading. Try again.', false);
      render();
      return;
    }
    afterClose(function(){
      var routed = false;
      if(kind === 'packs') routed = openShopTarget('packs');
      else if(kind === 'crystals') routed = openShopTarget('crystals');
      else if(kind === 'hearts') routed = openShopTarget('shop');
      else if(kind === 'bg'){
        if(enterYourHall()){
          try {
            if(typeof window.hohYourHallSetEventsTab === 'function') routed = window.hohYourHallSetEventsTab('bg') !== false;
            else if(typeof window.hohYourHallSwitchView === 'function') routed = window.hohYourHallSwitchView('events') !== false;
          } catch(_){ routed = false; }
          if(routed) focusSoon('[data-evtab="bg"],#hoh-yh-bg-door button');
        }
      } else {
        routed = openProfileTarget();
        if(routed) focusSoon('[data-yh-tab="profile"],#hoh-yh-collection button,.ffshop-grid button');
      }
      if(!routed) actionError(kind, 'That part of the Hall is still loading. Try again.', true);
    });
  }

  function refreshResources(){
    var seq = ++SYNC_SEQ;
    var W = window.HOHWallet || {};
    resourceActionState = { kind:'', state:'', message:'' };
    if(typeof W.sync !== 'function'){
      resourceSyncState = 'success'; resourceSyncMessage = 'Saved balances are ready.';
      if(isVisible()) render();
      return Promise.resolve(true);
    }
    resourceSyncState = 'loading'; resourceSyncMessage = '';
    if(isVisible()) render();
    var job;
    try { job = W.sync(true); } catch(_){ job = Promise.resolve(false); }
    return Promise.resolve(job).then(function(ok){
      if(seq !== SYNC_SEQ) return false;
      if(ok === false){
        resourceSyncState = 'error';
        resourceSyncMessage = 'Could not refresh your resources. The last saved balances are shown.';
      } else {
        resourceSyncState = 'success'; resourceSyncMessage = 'Balances updated.';
      }
      if(isVisible()) render();
      return ok !== false;
    }, function(){
      if(seq !== SYNC_SEQ) return false;
      resourceSyncState = 'error';
      resourceSyncMessage = 'Could not refresh your resources. The last saved balances are shown.';
      if(isVisible()) render();
      return false;
    });
  }

  /* R264: "Updates from Jan" — a single site-wide message shown where the
     old "Active buffs / DM's Satchel" card used to sit. The text lives in
     the hoh_site_config table (key 'jan_update'), readable by everyone and
     writable only by debug/god mode through the PIN-gated
     admin_set_site_config RPC. DM's Satchel is no longer surfaced here. */
  var janUpdate = null;
  try { var _ju = localStorage.getItem('hoh-jan-update-cache'); if(typeof _ju === 'string') janUpdate = _ju; } catch(_){}

  function fetchJanUpdate(){
    var sb = window.hohSupabaseClient;
    if(!sb || !sb.from) return Promise.resolve();
    try {
      return sb.from('hoh_site_config').select('value').eq('key','jan_update').maybeSingle().then(function(r){
        if(r && !r.error && r.data && typeof r.data.value === 'string'){
          janUpdate = r.data.value;
          try { localStorage.setItem('hoh-jan-update-cache', janUpdate); } catch(_){}
        }
      }, function(){});
    } catch(_){ return Promise.resolve(); }
  }

  function hohEtBuffsHtml(){
    var god = (typeof window.hohIsGodMode === 'function') && window.hohIsGodMode();
    var msg = (janUpdate != null) ? String(janUpdate) : '';
    /* Normal visitors only see the block when there IS a message. God mode
       always sees it (even empty) so the first message can be written. */
    if(!msg && !god) return '';
    var body = msg ? esc(msg) : 'No update yet — tap Edit to post one.';
    var editBtn = god
      ? '<button type="button" class="hoh-et-update-edit" data-et-focus="jan-edit" onclick="hohEtEditUpdate()">Edit</button>'
      : '';
    return '<div class="hoh-et-block hoh-et-buffs-block hoh-et-updates-block">' +
      '<div class="hoh-et-block-label">Updates from Jan</div>' +
      '<div class="hoh-et-buffs">' +
        '<div class="hoh-et-buff hoh-et-buff-update">' +
          '<span class="hoh-et-buff-glyph" aria-hidden="true">&#9993;&#xFE0E;</span>' +
          '<span class="hoh-et-buff-text">' +
            '<span class="hoh-et-buff-desc hoh-et-update-msg">' + body + '</span>' +
          '</span>' +
          editBtn +
        '</div>' +
      '</div>' +
    '</div>';
  }

  /* Debug/god publish flow. Prompts for the new text, then the admin PIN
     (cached in session like the other admin actions), and writes through
     the PIN-gated RPC so every visitor sees it on their next open. */
  window.hohEtEditUpdate = function(){
    var god = (typeof window.hohIsGodMode === 'function') && window.hohIsGodMode();
    if(!god) return;
    var cur = (janUpdate != null) ? String(janUpdate) : '';
    var next;
    try { next = window.prompt('Updates from Jan — shows for EVERYONE:', cur); } catch(_){ next = null; }
    if(next == null) return; /* cancelled */
    next = String(next);
    var sb = window.hohSupabaseClient;
    if(!sb || !sb.rpc){ try { alert('Database client unavailable.'); } catch(_){} return; }
    var email = (typeof window.hohYourHallEmail === 'function') ? (window.hohYourHallEmail() || '') : '';
    var pin = '';
    try { pin = sessionStorage.getItem('hoh-admin-pin') || ''; } catch(_){}
    if(!pin){
      try { pin = window.prompt('Enter your admin PIN to publish:') || ''; } catch(_){}
      if(pin){ try { sessionStorage.setItem('hoh-admin-pin', pin); } catch(_){} }
    }
    if(!pin) return;
    sb.rpc('admin_set_site_config', { p_email: email, p_pin: pin, p_key: 'jan_update', p_value: next }).then(function(r){
      if(r && r.error){ try { alert('Could not save: ' + (r.error.message || 'error') + '\n\nDid you run sql/R264-updates-from-jan.sql?'); } catch(_){} return; }
      var d = (r && r.data) || {};
      if(d.ok !== true){
        if(d.error === 'bad_pin'){ try { sessionStorage.removeItem('hoh-admin-pin'); } catch(_){} try { alert('Wrong admin PIN. Press Edit again to retry.'); } catch(_){} }
        else if(d.error === 'not_admin'){ try { alert('Only the admin account can edit this.'); } catch(_){} }
        else { try { alert('Save blocked: ' + (d.error || 'unknown') + '.\n\nIf the function is missing, run sql/R264-updates-from-jan.sql.'); } catch(_){} }
        return;
      }
      janUpdate = next;
      try { localStorage.setItem('hoh-jan-update-cache', next); } catch(_){}
      try { render(); } catch(_){}
      try { if(typeof window.hohShowHeartToast === 'function') window.hohShowHeartToast('Update published', 'Everyone sees it now.'); } catch(_){}
    }, function(){ try { alert('Save failed.'); } catch(_){} });
  };

  /* ============================================================
     R793 (Jan, 2026-09-22): THE GUIDE FOR THE NEW RULE.
     Tickets are no longer earned from quests and held in a wallet. Each
     of your own heroes takes ONE ticket a day, given on that hero's page
     in the main Hall. Hearts are favourites, so there is no heart balance
     to list. What you hold is the game's: packs, crystals, Battlegrounds
     entries. This render replaces the old one only in the live Hall;
     legacy mode (the R793B doors not installed yet) keeps render() below.
     Every count here is the doors' answer (hoh_hall_ticket_state and
     hoh_hall_explore_state), fetched when the guide opens. */
  function r793Live(){
    try { return (typeof window.hohR793Live === 'function') && window.hohR793Live(); } catch(_){ return false; }
  }
  function heroName(id){
    try {
      var all = (typeof window.hohGetAllCharactersFull === 'function') ? window.hohGetAllCharactersFull() : ((typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : []);
      for(var i = 0; i < (all || []).length; i++){ if(all[i] && String(all[i].id) === String(id)) return String(all[i].name || 'Your hero'); }
    } catch(_){}
    return 'Your hero';
  }
  window.hohEtOpenHero = function(id){
    afterClose(function(){
      /* the ticket is given in the main Hall, so leave Your Hall first */
      try { if(document.body.classList.contains('hoh-page-mode-yours') && typeof window.hohYhToggleClick === 'function') window.hohYhToggleClick(); } catch(_){}
      if(typeof window.hohOpenHallCard === 'function') window.hohOpenHallCard(String(id));
      else if(typeof window.hohOpenDetail === 'function') window.hohOpenDetail(String(id));
    });
  };
  window.hohEtOpenExplore = function(){
    afterClose(function(){
      if(typeof window.hohExploreEventOpen === 'function') window.hohExploreEventOpen();
    });
  };
  function r793TicketsHtml(){
    var Dd = window.HOHHallDoors;
    var st = Dd ? Dd.tickets.cached() : null;
    var rule = '<div class="hoh-et-daily-intro">Each of your heroes takes one raffle ticket a day. Open your hero in the Hall and tap Give today&rsquo;s ticket. The day turns at midnight Pacific time.</div>';
    if(!st){
      return rule + '<div class="hoh-et-sync is-loading" role="status" aria-live="polite"><span aria-hidden="true"></span>Checking today&rsquo;s tickets...</div>';
    }
    var ids = Object.keys(st.heroes);
    if(!ids.length){
      return rule + '<div class="hoh-et-done-banner">You have no heroes in the Hall yet. <span class="hoh-et-done-sub">Add one and it can take a ticket every day.</span></div>' +
        '<div class="hoh-et-cta-stack">' + ctaButton('Add Your Hero', "if(window.hohEtExit)hohEtExit('submit');", 'primary') + '</div>';
    }
    ids.sort(function(a, b){ return heroName(a).localeCompare(heroName(b)); });
    var left = 0;
    var rows = ids.map(function(id){
      var h = st.heroes[id];
      var done = !!h.applied_today;
      if(!done) left++;
      var name = esc(heroName(id));
      var tickets = (h.tickets | 0);
      var sub = tickets + (tickets === 1 ? ' ticket' : ' tickets') + (done ? '. Given today.' : '. Today&rsquo;s ticket is waiting.');
      var safeId = String(id).replace(/[^a-zA-Z0-9_-]/g, '');
      return '<div class="hoh-et-quest hoh-et-tkhero' + (done ? ' hoh-et-quest-done' : ' hoh-et-quest-clickable') + '"' +
        (done ? '' : ' role="button" tabindex="0" data-et-focus="tkhero-' + safeId + '" onclick="hohEtOpenHero(\'' + safeId + '\')" onkeydown="if(event.key===\'Enter\'||event.key===\' \'){event.preventDefault();hohEtOpenHero(\'' + safeId + '\');}"') + '>' +
        '<div class="hoh-et-quest-icon">' + (done ? '<span class="hoh-et-quest-check" aria-hidden="true">&check;</span>' : '<span class="hoh-tk-icon" aria-hidden="true"></span>') + '</div>' +
        '<div class="hoh-et-quest-body"><div class="hoh-et-quest-title">' + name + '</div><div class="hoh-et-quest-sub">' + sub + '</div></div>' +
        '<div class="hoh-et-quest-reward">' + (done ? '<span class="hoh-et-quest-reward-done">Given &check;</span>' : '<span class="hoh-et-quest-reward-open">Open</span>') + '</div>' +
      '</div>';
    }).join('');
    var head = left > 0 ? (left + (left === 1 ? ' ticket' : ' tickets') + ' to give') : 'All given today';
    var foot = (left === 0 && Dd) ? '<div class="hoh-et-daily-intro">' + esc(Dd.comeBackText(st.next_at)) + '</div>' : '';
    return rule + '<div class="hoh-et-block"><div class="hoh-et-block-label">' + head + '</div><div class="hoh-et-quests">' + rows + '</div></div>' + foot;
  }
  function r793ExploreHtml(){
    var Dd = window.HOHHallDoors;
    var x = Dd ? Dd.explore.cached() : null;
    if(!x) return '';
    var n = x.today | 0, lim = x.limit || 20;
    var done = !!x.done_today;
    return '<div class="hoh-et-block"><div class="hoh-et-block-label">Today&rsquo;s event</div><div class="hoh-et-quests">' +
      '<div class="hoh-et-quest' + (done ? ' hoh-et-quest-done' : ' hoh-et-quest-clickable') + '"' +
        (done ? '' : ' role="button" tabindex="0" data-et-focus="explore" onclick="hohEtOpenExplore()" onkeydown="if(event.key===\'Enter\'||event.key===\' \'){event.preventDefault();hohEtOpenExplore();}"') + '>' +
        '<div class="hoh-et-quest-icon">' + (done ? '<span class="hoh-et-quest-check" aria-hidden="true">&check;</span>' : '<span class="hoh-et-quest-num" aria-hidden="true">' + n + '</span>') + '</div>' +
        '<div class="hoh-et-quest-body"><div class="hoh-et-quest-title">Explore the Hall</div>' +
          /* R793.1 (Jan): no free-entry line here either; the server still pays it */
          '<div class="hoh-et-quest-sub">' + (done ? 'All ' + lim + ' heroes met today. ' + lim + ' more tomorrow.' : ('Meet ' + lim + ' heroes you have never seen. ' + n + ' of ' + lim + ' so far.')) + '</div></div>' +
        '<div class="hoh-et-quest-reward">' + (done ? '<span class="hoh-et-quest-reward-done">Done &check;</span>' : '<span class="hoh-et-quest-reward-open">Open</span>') + '</div>' +
      '</div></div></div>';
  }
  function renderR793(){
    var focusKey = rememberRenderFocus();
    var r = resourceSnapshot();
    var packDetail = r.packs > 0 ? 'Open one now. The rest stay saved.' : 'Buy a pack or claim the daily pack in the Shop.';
    ROOT.innerHTML =
      '<div class="hoh-et-backdrop"></div>' +
      '<div class="hoh-et-sheet" id="hoh-et-sheet" role="dialog" aria-modal="true" aria-labelledby="hoh-et-title" aria-describedby="hoh-et-intro" tabindex="-1">' +
        '<div class="hoh-et-header">' +
          '<div><div class="hoh-et-eyebrow">Your wallet</div><h1 id="hoh-et-title">Resources</h1></div>' +
          '<button type="button" class="hoh-et-close" data-et-focus="close-top" onclick="hohCloseEarnTickets()" aria-label="Close resources">&times;</button>' +
        '</div>' +
        '<div class="hoh-et-intro" id="hoh-et-intro">Your raffle tickets for today, and what you hold for the Battlegrounds.</div>' +
        '<div class="hoh-et-secondary hoh-et-r793-tickets">' +
          '<div class="hoh-et-section-head"><h2>Raffle tickets</h2><span>One a day per hero</span></div>' +
          r793TicketsHtml() +
          r793ExploreHtml() +
        '</div>' +
        '<section class="hoh-et-resource-section" aria-labelledby="hoh-et-resources-title"' + (resourceSyncState === 'loading' ? ' aria-busy="true"' : '') + '>' +
          '<div class="hoh-et-section-head"><h2 id="hoh-et-resources-title">For the Battlegrounds</h2><span>Tap a row to use it</span></div>' +
          '<div class="hoh-et-resources">' +
            resourceRow('packs', 'Unopened packs', r.packs, packDetail, r.packs > 0 ? 'Open one' : 'View packs') +
            resourceRow('crystals', 'Mythic crystals', r.crystals, 'Buy more or claim today\'s free crystal in the Shop.', 'View crystals') +
            resourceRow('bg', 'Battlegrounds entries', r.bg, 'Each entry starts one Battlegrounds run.', 'Go to battle') +
          '</div>' + resourceStatusHtml() +
        '</section>' +
        '<div class="hoh-et-secondary">' + hohEtBuffsHtml() + '</div>' +
        '<div class="hoh-et-howitworks-row">' +
          '<button type="button" class="hoh-et-howitworks" data-et-focus="rules" onclick="if(window.hohEtExit)hohEtExit(\'rules\');">' +
            '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>' +
            'How it works' +
          '</button>' +
          '<button type="button" class="hoh-et-howitworks-close" data-et-focus="close-bottom" onclick="hohCloseEarnTickets()">Close</button>' +
        '</div>' +
      '</div>';
    if(focusKey) requestAnimationFrame(function(){ restoreRenderFocus(focusKey); });
  }
  ['hoh:hall-tickets-changed', 'hoh:explore-state'].forEach(function(name){
    document.addEventListener(name, function(){
      if(ROOT && ROOT.classList.contains('hoh-et-open') && r793Live()){ try { render(); } catch(_){} }
    });
  });

  function render(){
    if(r793Live()) return renderR793();
    var focusKey = rememberRenderFocus();
    var st = getState();
    var totalDisplay = st.total + '/' + st.max;
    var notSignedIn = !(typeof window.hohYourHallEmail === 'function' && window.hohYourHallEmail());
    var headlineSub;
    if(notSignedIn){
      headlineSub = 'Submit a hero to unlock Your Hall. Tickets carry over once you sign in.';
    } else if(st.bothDone){
      headlineSub = 'You earned all 4 today. Now go spend the tickets you can distribute.';
    } else {
      headlineSub = 'Up to 4 tickets a day. The submission ticket auto-pins; the other 3 are yours to spend.';
    }

    /* Earn section — the two daily quests, explained once. When both
       are done it collapses to one calm line. The Hearts hidden block
       was removed by request. */
    var earnHtml;
    if(st.bothDone){
      earnHtml = '<div class="hoh-et-done-banner">Daily quests complete &check; <span class="hoh-et-done-sub">Fresh tickets tomorrow.</span></div>';
    } else {
      earnHtml =
        '<div class="hoh-et-block">' +
          '<div class="hoh-et-block-label">Earn today</div>' +
          '<div class="hoh-et-quests">' +
            questRowHtml({
              num: 1,
              done: st.submitted,
              title: 'Submit a hero',
              sub: 'A new character for the Hall. Auto-pins one ticket to that hero.',
              reward: 1,
              interactive: !st.submitted,
              onclick: "if(window.hohEtExit)hohEtExit('submit');"
            }) +
            questRowHtml({
              num: 2,
              done: st.tinder,
              title: 'Explore the Hall',
              sub: (st.tinder
                ? 'You wandered the Hall today.'
                : 'Heart heroes from Fresh Faces and claim the milestone tickets.'),
              reward: 3,
              interactive: !st.tinder,
              onclick: "if(window.hohEtExit)hohEtExit('explore');"
            }) +
          '</div>' +
        '</div>';
    }

    /* R256: the items you hold — the Oath's countdown lives HERE too,
       not only in the corner chip, plus any charms and scrolls. Reads
       the shared inventory helpers Fresh Faces exposes; renders
       nothing when the satchel is empty. */
    var itemsHtml = '';
    try{
      var invRows = [];
      var oath = (typeof window.hohInventoryOathStatus === 'function') ? window.hohInventoryOathStatus() : null;
      if(oath){
        var oleft = Math.max(0, 5 - oath.daysHeld);
        invRows.push({
          ic:'&#10022;',
          t:"Patron's Oath" + (oath.count > 1 ? ' &times;' + oath.count : ''),
          s: oath.claimable ? 'Held five days. Claim it at the Sage’s Bazaar.'
                            : ('Hold it ' + oleft + ' more day' + (oleft === 1 ? '' : 's') + ', then claim it.'),
          st: oath.claimable ? 'Ready' : (oleft + 'd left')
        });
      }
      var chN = (typeof window.hohInventoryCount === 'function') ? (window.hohInventoryCount('lonefireCharm') | 0) : 0;
      if(chN > 0) invRows.push({ ic:'&#128293;', t:'Lonefire Charm' + (chN > 1 ? ' &times;' + chN : ''), s:'The next time your hearts run out, the charm steps in.', st:'Armed' });
      var scN = (typeof window.hohInventoryCount === 'function') ? (window.hohInventoryCount('scrollOfLineage') | 0) : 0;
      if(scN > 0) invRows.push({ ic:'&#128220;', t:'Scroll of Lineage' + (scN > 1 ? ' &times;' + scN : ''), s:'One free hero unlock, on the house.', st:'Held' });
      if(invRows.length){
        if(!document.getElementById('hoh-et-items-css')){
          var etst = document.createElement('style'); etst.id = 'hoh-et-items-css';
          etst.textContent =
            '.hoh-et-items{display:flex;flex-direction:column;gap:8px}'+
            '.hoh-et-item{display:flex;align-items:center;gap:11px;padding:10px 12px;border-radius:10px;'+
              'border:1px solid rgba(200,164,94,.28);background:rgba(200,164,94,.05)}'+
            '.hoh-et-item-ic{font-size:15px;color:#e8c97a;flex:none}'+
            '.hoh-et-item-tx{flex:1;min-width:0;display:flex;flex-direction:column;gap:2px}'+
            '.hoh-et-item-tx b{font-family:\'Cinzel\',serif;font-size:12px;letter-spacing:.06em;color:#f2d98e;font-weight:700}'+
            '.hoh-et-item-tx i{font-family:\'Cormorant Garamond\',Georgia,serif;font-size:13.5px;color:#a09aae;line-height:1.35}'+
            '.hoh-et-item-st{flex:none;font-family:\'Cinzel\',serif;font-size:9.5px;letter-spacing:.1em;text-transform:uppercase;'+
              'color:#e8c97a;border:1px solid rgba(200,164,94,.45);border-radius:999px;padding:4px 9px}';
          (document.head||document.documentElement).appendChild(etst);
        }
        itemsHtml =
          '<div class="hoh-et-block">' +
            '<div class="hoh-et-block-label">Items you hold</div>' +
            '<div class="hoh-et-items">' +
              invRows.map(function(r){
                return '<div class="hoh-et-item"><span class="hoh-et-item-ic">' + r.ic + '</span>' +
                  '<span class="hoh-et-item-tx"><b>' + r.t + '</b><i>' + r.s + '</i></span>' +
                  '<span class="hoh-et-item-st">' + r.st + '</span></div>';
              }).join('') +
            '</div>' +
          '</div>';
      }
    }catch(_){}

    ROOT.innerHTML =
      '<div class="hoh-et-backdrop"></div>' +
      '<div class="hoh-et-sheet" id="hoh-et-sheet" role="dialog" aria-modal="true" aria-labelledby="hoh-et-title" aria-describedby="hoh-et-intro" tabindex="-1">' +
        '<div class="hoh-et-header">' +
          '<div><div class="hoh-et-eyebrow">Your wallet</div><h1 id="hoh-et-title">Resources</h1></div>' +
          '<button type="button" class="hoh-et-close" data-et-focus="close-top" onclick="hohCloseEarnTickets()" aria-label="Close resources">&times;</button>' +
        '</div>' +
        '<div class="hoh-et-intro" id="hoh-et-intro">See what you hold and where to use it.</div>' +
        resourceGuideHtml() +
        '<div class="hoh-et-secondary">' +
          '<div class="hoh-et-section-head"><h2>Daily tickets</h2><span>' + totalDisplay + ' today</span></div>' +
          '<div class="hoh-et-daily-intro">' + headlineSub + '</div>' +
          earnHtml +
          ctaHtml(st) +
          itemsHtml +
          hohEtBuffsHtml() +
        '</div>' +

        /* Single "How it works" link replaces the two accordion
           explainers. Opens the main rules modal which now covers
           hearts AND tickets in one place — fewer collapsed sections,
           one source of truth. A second Close button sits beneath it
           so the user always has a visible exit without hunting for
           the corner X. */
        '<div class="hoh-et-howitworks-row">' +
          '<button type="button" class="hoh-et-howitworks" data-et-focus="rules" onclick="if(window.hohEtExit)hohEtExit(\'rules\');">' +
            '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>' +
            'How it works' +
          '</button>' +
          '<button type="button" class="hoh-et-howitworks-close" data-et-focus="close-bottom" onclick="hohCloseEarnTickets()">Close</button>' +
        '</div>' +
      '</div>';
    if(focusKey) requestAnimationFrame(function(){ restoreRenderFocus(focusKey); });
  }

  /* Live progress: when a heart at fresh-faces bumps the wander
     counter, re-render IF this modal is currently open so the user
     sees the count tick up in real time. We also listen for the
     broader ticket-state-changed event (fired when a wander
     milestone awards a ticket) so the 0/4 → 1/4 → 2/4 transition
     reflects immediately in the daily-tickets panel. Cheap — render
     is just innerHTML replacement. */
  document.addEventListener('hoh:ff-quest-progress', function(){
    if(ROOT && ROOT.classList.contains('hoh-et-open')){
      try { render(); } catch(_){}
    }
  });
  document.addEventListener('hoh:ticket-state-changed', function(){
    if(ROOT && ROOT.classList.contains('hoh-et-open')){
      try { render(); } catch(_){}
    }
  });
  /* R64: wallet changes (apply ticket, earn ticket → unspent) should
     re-paint the panel too so the unspent badge updates live. */
  document.addEventListener('hoh:unspent-tickets-changed', function(){
    if(ROOT && ROOT.classList.contains('hoh-et-open')){
      try { render(); } catch(_){}
    }
  });
  document.addEventListener('hoh:ticket-applied', function(){
    if(ROOT && ROOT.classList.contains('hoh-et-open')){
      try { render(); } catch(_){}
    }
  });

  /* Currency and pack changes all repaint one resource list. Wallet sync is
     the authoritative catch-all; the narrower events keep local spends and
     grants feeling immediate while that round trip is still in flight. */
  ['hoh:wallet-synced','hoh:packs-changed','hoh:crystals-changed','hoh:bg-tickets-changed','hoh:hearts-bumped'].forEach(function(name){
    document.addEventListener(name, function(){
      if(ROOT && ROOT.classList.contains('hoh-et-open')){
        try { render(); } catch(_){}
      }
    });
  });

  window.hohOpenEarnTickets = async function(opts){
    opts = opts || {};
    ensureRoot();
    var wasVisible = ROOT.style.display !== 'none';
    if(CLOSE_TIMER){ clearTimeout(CLOSE_TIMER); CLOSE_TIMER = 0; }
    if(!wasVisible){
      LAST_FOCUS = document.activeElement && document.activeElement !== document.body ? document.activeElement : null;
      BODY_OVERFLOW = document.body.style.overflow || '';
    }
    ROOT.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    if(!opts.skipResourceSync){ resourceSyncState = 'loading'; resourceSyncMessage = ''; }
    render();
    requestAnimationFrame(function(){
      ROOT.classList.add('hoh-et-open');
      if(!wasVisible) requestAnimationFrame(focusDialog);
    });
    /* R264: pull the latest "Updates from Jan" message, then repaint so a
       freshly-published note shows without a reload. */
    try { fetchJanUpdate().then(function(){ if(ROOT && ROOT.classList.contains('hoh-et-open')) render(); }); } catch(_){}
    /* Fetch raffle state so the modal updates with fresh quest data
       shortly after opening. */
    var jobs = [];
    if(!opts.skipResourceSync) jobs.push(refreshResources());
    /* R793: the live guide reads today's tickets and Explore from their doors. */
    if(r793Live() && window.HOHHallDoors){
      try { jobs.push(window.HOHHallDoors.tickets.state(true)); } catch(_){}
      try { jobs.push(window.HOHHallDoors.explore.state()); } catch(_){}
    } else if(typeof window.hohFetchRaffleState === 'function') jobs.push(window.hohFetchRaffleState());
    if(jobs.length){
      try { await Promise.all(jobs); } catch(e){}
      if(isVisible()) render();
    }
  };
})();
